import React, { useEffect, useState } from 'react';

import { CarouselArrow } from '../CarouselArrow/CarouselArrow';
import { CarouselDots } from '../CarouselDots/CarouselDots';
import { useCarousel } from '../../hooks/useCarousel';
import { useInView } from '../../hooks/useInView';

type SplitCarouselProps<T> = {
    slides: T[];
    classPrefix: string;
    renderVisual: (slide: T, index: number) => React.ReactNode;
    renderText: (slide: T, index: number) => React.ReactNode;
    autoplayMs?: number;
    previousLabel?: string;
    nextLabel?: string;
    dotLabel?: (index: number) => string;
    arrowFocusColor?: string;
    dotColor?: string;
};

export function SplitCarousel<T>({
    slides,
    classPrefix,
    renderVisual,
    renderText,
    autoplayMs,
    previousLabel = 'Previous',
    nextLabel = 'Next',
    dotLabel = (i: number) => `Go to slide ${i + 1}`,
    arrowFocusColor,
    dotColor,
}: SplitCarouselProps<T>) {
    const total = slides.length;
    const { index, setIndex, goPrev, goNext } = useCarousel({ total, autoplayMs });
    const [ref, inView] = useInView<HTMLDivElement>();
    const [mounted, setMounted] = useState(false);

    useEffect(() => {
        const id = window.requestAnimationFrame(() => setMounted(true));
        return () => window.cancelAnimationFrame(id);
    }, []);

    if (total === 0) { return null; }

    const current = slides[index];
    const readyClass = mounted && inView ? ` ${classPrefix}__grid--in-view` : '';

    return (
        <div ref={ref} className={`${classPrefix}__grid${readyClass}`}>
            <div className={`${classPrefix}__visual-card`}>
                {slides.map((slide, i) => (
                    <div
                        key={i}
                        className={`${classPrefix}__visual-layer${i === index ? ` ${classPrefix}__visual-layer--active` : ''}`}
                        aria-hidden={i !== index}
                    >
                        {renderVisual(slide, i)}
                    </div>
                ))}
            </div>
            <div className={`${classPrefix}__text-card`}>
                {renderText(current, index)}
                <div className={`${classPrefix}__controls`}>
                    <CarouselArrow
                        direction="prev"
                        ariaLabel={previousLabel}
                        onClick={goPrev}
                        focusColor={arrowFocusColor}
                    />
                    <CarouselDots
                        count={total}
                        activeIndex={index}
                        onSelect={setIndex}
                        label={dotLabel}
                        color={dotColor}
                    />
                    <CarouselArrow
                        direction="next"
                        ariaLabel={nextLabel}
                        onClick={goNext}
                        focusColor={arrowFocusColor}
                    />
                </div>
            </div>
        </div>
    );
}
